import { useEffect, useRef, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";

const blobCache = new Map();

function FotoThumb({ asetId, foto, onView, onDelete, isAdmin }) {
  const key = `${asetId}/${foto.id}`;
  const [src, setSrc] = useState(() => blobCache.get(key) || null);

  useEffect(() => {
    let alive = true;
    if (blobCache.has(key)) { setSrc(blobCache.get(key)); return; }
    api.get(`/aset/${asetId}/foto/${foto.id}`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        blobCache.set(key, url);
        if (alive) setSrc(url);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [key, asetId, foto.id]);

  return (
    <div className="relative group shrink-0" data-testid={`aset-foto-${foto.id}`}>
      {src ? (
        <img src={src} alt="Foto aset" onClick={() => onView(src)}
          className="h-16 w-16 rounded-md object-cover border border-slate-200 cursor-pointer hover:opacity-80 transition-opacity" />
      ) : (
        <div className="h-16 w-16 rounded-md bg-slate-100 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        </div>
      )}
      {isAdmin && (
        <button onClick={() => onDelete(foto)} data-testid={`delete-aset-foto-${foto.id}`} title="Hapus foto"
          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default function AsetFotoStrip({ aset, onChanged }) {
  const { user } = useAuth();
  const isAdmin = (user?.role || "admin") === "admin";
  const [uploading, setUploading] = useState(false);
  const [viewSrc, setViewSrc] = useState(null);
  const inputRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/aset/${aset.id}/foto`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto kondisi aset diunggah");
      onChanged(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (foto) => {
    try {
      const { data } = await api.delete(`/aset/${aset.id}/foto/${foto.id}`);
      toast.success("Foto dihapus");
      onChanged(data);
    } catch {
      toast.error("Gagal menghapus foto");
    }
  };

  return (
    <div className="mt-2 pl-7" data-testid={`aset-foto-strip-${aset.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {(aset.foto || []).map((f) => (
          <FotoThumb key={f.id} asetId={aset.id} foto={f} isAdmin={isAdmin}
            onView={setViewSrc} onDelete={remove} />
        ))}
        {isAdmin && (
          <>
            <input type="file" ref={inputRef} accept="image/jpeg,image/png,image/webp"
              onChange={upload} className="hidden" data-testid={`aset-foto-input-${aset.id}`} />
            <button onClick={() => inputRef.current?.click()} disabled={uploading}
              data-testid={`upload-aset-foto-${aset.id}`} title="Unggah foto kondisi"
              className="h-16 w-16 rounded-md border-2 border-dashed border-blue-200 text-blue-500 hover:bg-blue-50 flex flex-col items-center justify-center gap-0.5 transition-colors">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              <span className="text-[9px] font-semibold">Foto</span>
            </button>
          </>
        )}
        {!isAdmin && !(aset.foto || []).length && (
          <span className="text-xs text-slate-400">Belum ada foto kondisi.</span>
        )}
      </div>

      <Dialog open={!!viewSrc} onOpenChange={(v) => !v && setViewSrc(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="aset-foto-viewer">
          <DialogTitle className="sr-only">Foto kondisi aset</DialogTitle>
          {viewSrc && <img src={viewSrc} alt="Foto kondisi aset" className="w-full max-h-[80vh] object-contain rounded-md" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
